import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withKeys as _withKeys, withDirectives as _withDirectives, TransitionGroup as _TransitionGroup, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "layer-panel" }
const _hoisted_2 = { class: "layer-panel__header" }
const _hoisted_3 = { class: "layer-panel__content" }
const _hoisted_4 = { class: "layer-list" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = ["title", "onClick"]
const _hoisted_7 = ["title", "onClick"]
const _hoisted_8 = ["onDblclick"]
const _hoisted_9 = { class: "layer-item__text" }
const _hoisted_10 = {
  key: 0,
  class: "layer-item__count"
}
const _hoisted_11 = ["onKeyup", "onBlur"]
const _hoisted_12 = { class: "layer-item__actions" }
const _hoisted_13 = ["onClick", "title"]
const _hoisted_14 = ["onClick"]
const _hoisted_15 = ["onClick"]
const _hoisted_16 = ["onClick"]
const _hoisted_17 = ["onClick", "disabled"]
const _hoisted_18 = ["onClick", "disabled"]
const _hoisted_19 = ["onClick", "disabled"]
const _hoisted_20 = {
  key: 2,
  class: "layer-item__opacity"
}
const _hoisted_21 = ["value", "onInput"]
const _hoisted_22 = { class: "layer-panel__controls" }
const _hoisted_23 = ["disabled"]
const _hoisted_24 = { class: "color-picker-header" }
const _hoisted_25 = { class: "color-picker-content" }
const _hoisted_26 = { class: "color-preset-grid" }
const _hoisted_27 = ["onClick"]
const _hoisted_28 = { class: "color-picker-custom" }

import { ref, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNotifications } from '@/composables/useNotifications';
import type { AnnotationLayer } from '@/types/annotation';

interface Props {
  layers: AnnotationLayer[];
  activeLayerId: string | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'LayerPanel',
  props: {
    layers: {},
    activeLayerId: {}
  },
  emits: ["layer-create", "layer-delete", "layer-rename", "layer-select", "layer-toggle-visibility", "layer-toggle-lock", "layer-change-color", "layer-change-opacity", "layer-reorder", "layer-duplicate", "layer-merge", "close"],
  setup(__props: any, { emit: __emit }) {

// @ts-ignore
const props = __props;
const emit = __emit;

const { t } = useI18n();
const { notifyConfirm } = useNotifications();

// State
const editingLayerId = ref<string | null>(null);
const editingLayerName = ref('');
const activeMenuLayerId = ref<string | null>(null);
const showOpacityControl = ref(false);
const showColorPicker = ref(false);
const selectedLayerForColor = ref<string | null>(null);
const customColor = ref('#007bff');
const hasSelectedText = ref(false);

// Callback ref for edit input
const setEditInputRef = (el: any) => {
  if (el && !hasSelectedText.value) {
    nextTick(() => {
      el.focus();
      el.select();
      hasSelectedText.value = true;
    });
  }
};

// Color presets for layer color-coding
const colorPresets = [
  '#007bff', // Blue
  '#dc3545', // Red
  '#28a745', // Green
  '#ffc107', // Yellow
  '#6f42c1', // Purple
  '#fd7e14', // Orange
  '#20c997', // Teal
  '#e83e8c', // Pink
  '#6c757d', // Gray
  '#17a2b8', // Cyan
  '#343a40', // Dark
  '#f8f9fa', // Light
];

// Computed
const sortedLayers = computed(() => {
  return [...props.layers].sort((a, b) => b.order - a.order);
});

const visibleLayersCount = computed(() => {
  return props.layers.filter((l: AnnotationLayer) => l.visible).length;
});

// Methods
const selectLayer = (layerId: string) => {
  emit('layer-select', layerId);
  activeMenuLayerId.value = null;
};

const toggleVisibility = (layerId: string) => {
  emit('layer-toggle-visibility', layerId);
};

const toggleLock = (layerId: string) => {
  emit('layer-toggle-lock', layerId);
};

const createNewLayer = () => {
  const layerNumber = props.layers.length + 1;
  const defaultName = `${t('annotation.layer')} ${layerNumber}`;
  emit('layer-create', defaultName);
};

const startRenameLayer = (layerId: string, currentName: string) => {
  editingLayerId.value = layerId;
  editingLayerName.value = currentName;
  activeMenuLayerId.value = null;
  hasSelectedText.value = false;
  // Focus and select handled by setEditInputRef callback
};

const saveLayerName = (layerId: string) => {
  if (editingLayerName.value.trim()) {
    emit('layer-rename', layerId, editingLayerName.value.trim());
  }
  editingLayerId.value = null;
  editingLayerName.value = '';
  hasSelectedText.value = false;
};

const cancelEditLayerName = () => {
  editingLayerId.value = null;
  editingLayerName.value = '';
  hasSelectedText.value = false;
};

const deleteLayer = (layerId: string) => {
  if (props.layers.length <= 1) {
    return; // Prevent deletion of last layer
  }
  notifyConfirm(t('annotation.confirmDeleteLayer'), () => {
    emit('layer-delete', layerId);
    activeMenuLayerId.value = null;
  });
};

const duplicateLayer = (layerId: string) => {
  emit('layer-duplicate', layerId);
  activeMenuLayerId.value = null;
};

const changeLayerColor = (layerId: string) => {
  selectedLayerForColor.value = layerId;
  showColorPicker.value = true;
  activeMenuLayerId.value = null;
};

const applyLayerColor = (color: string) => {
  if (selectedLayerForColor.value) {
    emit('layer-change-color', selectedLayerForColor.value, color);
  }
  showColorPicker.value = false;
  selectedLayerForColor.value = null;
};

const updateLayerOpacity = (layerId: string, event: Event) => {
  const target = event.target as HTMLInputElement;
  const opacity = parseInt(target.value) / 100;
  emit('layer-change-opacity', layerId, opacity);
};

const moveLayerUp = (layerId: string) => {
  emit('layer-reorder', layerId, 'up');
  activeMenuLayerId.value = null;
};

const moveLayerDown = (layerId: string) => {
  emit('layer-reorder', layerId, 'down');
  activeMenuLayerId.value = null;
};

const isTopLayer = (layerId: string) => {
  const layer = props.layers.find((l: AnnotationLayer) => l.id === layerId);
  if (!layer) return true;
  return layer.order === Math.max(...props.layers.map((l: AnnotationLayer) => l.order));
};

const isBottomLayer = (layerId: string) => {
  const layer = props.layers.find((l: AnnotationLayer) => l.id === layerId);
  if (!layer) return true;
  return layer.order === Math.min(...props.layers.map((l: AnnotationLayer) => l.order));
};

const toggleLayerMenu = (layerId: string) => {
  activeMenuLayerId.value = activeMenuLayerId.value === layerId ? null : layerId;
};

const mergeVisibleLayers = () => {
  const visibleLayerIds = props.layers.filter((l: AnnotationLayer) => l.visible).map((l: AnnotationLayer) => l.id);
  if (visibleLayerIds.length < 2) return;
  
  notifyConfirm(t('annotation.confirmMergeLayers'), () => {
    emit('layer-merge', visibleLayerIds);
  });
};

// Close menu when clicking outside
const closeMenus = () => {
  activeMenuLayerId.value = null;
};

// Close menus on outside click
if (typeof window !== 'undefined') {
  window.addEventListener('click', closeMenus);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("h3", null, [
        _cache[11] || (_cache[11] = _createElementVNode("i", { class: "fas fa-layer-group" }, null, -1)),
        _createTextVNode(" " + _toDisplayString(_ctx.$t('annotation.layers')), 1)
      ]),
      _createElementVNode("button", {
        class: "btn-close",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('close')))
      }, [...(_cache[12] || (_cache[12] = [
        _createElementVNode("i", { class: "fas fa-times" }, null, -1)
      ]))])
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _createVNode(_TransitionGroup, { name: "layer-fade" }, {
          default: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedLayers.value, (layer) => {
              return (_openBlock(), _createElementBlock("div", {
                key: layer.id,
                class: _normalizeClass([
              'layer-item',
              { 'layer-item--active': layer.id === _ctx.activeLayerId },
              { 'layer-item--locked': layer.locked },
            ]),
                onClick: ($event: any) => (selectLayer(layer.id))
              }, [
                _createElementVNode("button", {
                  class: "layer-item__action",
                  title: layer.visible ? _ctx.$t('annotation.hideLayer') : _ctx.$t('annotation.showLayer'),
                  onClick: _withModifiers(($event: any) => (toggleVisibility(layer.id)), ["stop"])
                }, [
                  _createElementVNode("i", {
                    class: _normalizeClass(layer.visible ? 'fas fa-eye' : 'fas fa-eye-slash')
                  }, null, 2)
                ], 8, _hoisted_6),
                _createElementVNode("button", {
                  class: "layer-item__action",
                  title: layer.locked ? _ctx.$t('annotation.unlockLayer') : _ctx.$t('annotation.lockLayer'),
                  onClick: _withModifiers(($event: any) => (toggleLock(layer.id)), ["stop"])
                }, [
                  _createElementVNode("i", {
                    class: _normalizeClass(layer.locked ? 'fas fa-lock' : 'fas fa-lock-open')
                  }, null, 2)
                ], 8, _hoisted_7),
                (!editingLayerId.value || editingLayerId.value !== layer.id)
                  ? (_openBlock(), _createElementBlock("div", {
                      key: 0,
                      class: "layer-item__name",
                      onDblclick: _withModifiers(($event: any) => (startRenameLayer(layer.id, layer.name)), ["stop"])
                    }, [
                      _createElementVNode("span", {
                        class: "layer-item__color",
                        style: _normalizeStyle({ backgroundColor: layer.color })
                      }, null, 4),
                      _createElementVNode("span", _hoisted_9, _toDisplayString(layer.name), 1),
                      (layer.objectCount !== undefined)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_10, "(" + _toDisplayString(layer.objectCount) + ")", 1))
                        : _createCommentVNode("", true)
                    ], 40, _hoisted_8))
                  : _withDirectives((_openBlock(), _createElementBlock("input", {
                      key: 1,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((editingLayerName).value = $event)),
                      type: "text",
                      class: "layer-item__input",
                      onKeyup: [
                        _withKeys(($event: any) => (saveLayerName(layer.id)), ["enter"]),
                        _withKeys(cancelEditLayerName, ["esc"])
                      ],
                      onBlur: ($event: any) => (saveLayerName(layer.id)),
                      onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"])),
                      ref_for: true,
                      ref: (el) => setEditInputRef(el),
                      autofocus: ""
                    }, null, 40, _hoisted_11)), [
                      [_vModelText, editingLayerName.value]
                    ]),
                _createElementVNode("div", _hoisted_12, [
                  _createElementVNode("button", {
                    class: "layer-item__menu",
                    onClick: _withModifiers(($event: any) => (toggleLayerMenu(layer.id)), ["stop"]),
                    title: _ctx.$t('annotation.layerActions')
                  }, [...(_cache[13] || (_cache[13] = [
                    _createElementVNode("i", { class: "fas fa-ellipsis-v" }, null, -1)
                  ]))], 8, _hoisted_13),
                  (activeMenuLayerId.value === layer.id)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: "layer-menu",
                        onClick: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["stop"]))
                      }, [
                        _createElementVNode("button", {
                          onClick: ($event: any) => (startRenameLayer(layer.id, layer.name))
                        }, [
                          _cache[14] || (_cache[14] = _createElementVNode("i", { class: "fas fa-edit" }, null, -1)),
                          _createTextVNode(" " + _toDisplayString(_ctx.$t('annotation.rename')), 1)
                        ], 8, _hoisted_14),
                        _createElementVNode("button", {
                          onClick: ($event: any) => (duplicateLayer(layer.id))
                        }, [
                          _cache[15] || (_cache[15] = _createElementVNode("i", { class: "fas fa-copy" }, null, -1)),
                          _createTextVNode(" " + _toDisplayString(_ctx.$t('annotation.duplicate')), 1)
                        ], 8, _hoisted_15),
                        _createElementVNode("button", {
                          onClick: ($event: any) => (changeLayerColor(layer.id))
                        }, [
                          _cache[16] || (_cache[16] = _createElementVNode("i", { class: "fas fa-palette" }, null, -1)),
                          _createTextVNode(" " + _toDisplayString(_ctx.$t('annotation.changeColor')), 1)
                        ], 8, _hoisted_16),
                        _createElementVNode("button", {
                          onClick: ($event: any) => (moveLayerUp(layer.id)),
                          disabled: isTopLayer(layer.id)
                        }, [
                          _cache[17] || (_cache[17] = _createElementVNode("i", { class: "fas fa-arrow-up" }, null, -1)),
                          _createTextVNode(" " + _toDisplayString(_ctx.$t('annotation.moveUp')), 1)
                        ], 8, _hoisted_17),
                        _createElementVNode("button", {
                          onClick: ($event: any) => (moveLayerDown(layer.id)),
                          disabled: isBottomLayer(layer.id)
                        }, [
                          _cache[18] || (_cache[18] = _createElementVNode("i", { class: "fas fa-arrow-down" }, null, -1)),
                          _createTextVNode(" " + _toDisplayString(_ctx.$t('annotation.moveDown')), 1)
                        ], 8, _hoisted_18),
                        _cache[20] || (_cache[20] = _createElementVNode("hr", null, null, -1)),
                        _createElementVNode("button", {
                          onClick: ($event: any) => (deleteLayer(layer.id)),
                          class: "layer-menu__danger",
                          disabled: _ctx.layers.length <= 1
                        }, [
                          _cache[19] || (_cache[19] = _createElementVNode("i", { class: "fas fa-trash" }, null, -1)),
                          _createTextVNode(" " + _toDisplayString(_ctx.$t('annotation.deleteLayer')), 1)
                        ], 8, _hoisted_19)
                      ]))
                    : _createCommentVNode("", true)
                ]),
                (layer.id === _ctx.activeLayerId && showOpacityControl.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                      _createElementVNode("label", null, _toDisplayString(_ctx.$t('annotation.opacity')) + ": " + _toDisplayString(Math.round(layer.opacity * 100)) + "%", 1),
                      _createElementVNode("input", {
                        type: "range",
                        min: "0",
                        max: "100",
                        value: layer.opacity * 100,
                        onInput: ($event: any) => (updateLayerOpacity(layer.id, $event)),
                        onClick: _cache[4] || (_cache[4] = _withModifiers(() => {}, ["stop"]))
                      }, null, 40, _hoisted_21)
                    ]))
                  : _createCommentVNode("", true)
              ], 10, _hoisted_5))
            }), 128))
          ]),
          _: 1
        })
      ]),
      _createElementVNode("button", {
        class: "layer-panel__add-btn",
        onClick: createNewLayer
      }, [
        _cache[21] || (_cache[21] = _createElementVNode("i", { class: "fas fa-plus" }, null, -1)),
        _createTextVNode(" " + _toDisplayString(_ctx.$t('annotation.newLayer')), 1)
      ]),
      _createElementVNode("div", _hoisted_22, [
        _createElementVNode("button", {
          onClick: _cache[5] || (_cache[5] = ($event: any) => (showOpacityControl.value = !showOpacityControl.value)),
          class: _normalizeClass({ active: showOpacityControl.value })
        }, [
          _cache[22] || (_cache[22] = _createElementVNode("i", { class: "fas fa-adjust" }, null, -1)),
          _createTextVNode(" " + _toDisplayString(_ctx.$t('annotation.opacity')), 1)
        ], 2),
        _createElementVNode("button", {
          onClick: mergeVisibleLayers,
          disabled: visibleLayersCount.value < 2
        }, [
          _cache[23] || (_cache[23] = _createElementVNode("i", { class: "fas fa-object-group" }, null, -1)),
          _createTextVNode(" " + _toDisplayString(_ctx.$t('annotation.mergeLayers')), 1)
        ], 8, _hoisted_23)
      ])
    ]),
    (showColorPicker.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "modal-overlay",
          onClick: _cache[10] || (_cache[10] = ($event: any) => (showColorPicker.value = false))
        }, [
          _createElementVNode("div", {
            class: "color-picker-dialog",
            onClick: _cache[9] || (_cache[9] = _withModifiers(() => {}, ["stop"]))
          }, [
            _createElementVNode("div", _hoisted_24, [
              _createElementVNode("h4", null, _toDisplayString(_ctx.$t('annotation.selectLayerColor')), 1),
              _createElementVNode("button", {
                class: "btn-close",
                onClick: _cache[6] || (_cache[6] = ($event: any) => (showColorPicker.value = false))
              }, [...(_cache[24] || (_cache[24] = [
                _createElementVNode("i", { class: "fas fa-times" }, null, -1)
              ]))])
            ]),
            _createElementVNode("div", _hoisted_25, [
              _createElementVNode("div", _hoisted_26, [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(colorPresets, (color) => {
                  return _createElementVNode("button", {
                    key: color,
                    class: "color-preset",
                    style: _normalizeStyle({ backgroundColor: color }),
                    onClick: ($event: any) => (applyLayerColor(color))
                  }, null, 12, _hoisted_27)
                }), 64))
              ]),
              _createElementVNode("div", _hoisted_28, [
                _createElementVNode("label", null, _toDisplayString(_ctx.$t('annotation.customColor')), 1),
                _withDirectives(_createElementVNode("input", {
                  type: "color",
                  "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((customColor).value = $event)),
                  onChange: _cache[8] || (_cache[8] = ($event: any) => (applyLayerColor(customColor.value)))
                }, null, 544), [
                  [_vModelText, customColor.value]
                ])
              ])
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})